<template>
  <div class="profile__wrapper">
    <template v-if="structure">
      <div class="categorisation categorisation__wrapper">
        <div
          class="profile__section"
          :style="{ order: group.order_position }"
          v-for="(group, id_0) in structure"
          :key="`${group.uuid}${id_0}`"
        >
          <h4 class="title-h4 profile__title" v-if="!!group.title">
            {{ group.title }}
          </h4>
          <div
            class="categorisation__block"
            :style="{ order: subgroup.order_position }"
            v-for="(subgroup, id_1) in group.subgroups"
            :key="`${subgroup.uuid}${id_1}`"
          >
            <h5 class="title-h5 profile__subtitle" v-if="!!subgroup.title">
              {{ subgroup.title }}:
            </h5>
            <div
              :class="[
                subgroup.criterias[0].type === 'images'
                  ? 'image__grid'
                  : 'profile__grid',
              ]"
            >
              <div
                v-for="(item, id_2) in subgroup.criterias"
                :key="`${item.uuid}${id_2}`"
                :class="`${item.classnames}`"
                :style="{ order: item.order_position }"
                :id="item.uuid"
              >
                <text-field
                  v-if="['text', 'number', 'rating'].includes(item.type)"
                  v-model="item['value']"
                  :is-error="item.error == true"
                  :stars-read-only="editIsDisabled(item.uuid)"
                  :title="item.title"
                  :is-rate="item.type === 'rating'"
                  :is-icon="item.type === 'text_icon'"
                  :value-mask="item.mask"
                  :is-inactive="editIsDisabled(item.uuid)"
                  icon-name="actual"
                  :can-comment="canComment"
                  is-commentable
                  :uuid="item.uuid"
                />
                <FileUpload
                  v-else-if="['file', 'files'].includes(item.type)"
                  :id="item.uuid"
                  v-model="item['value']"
                  :title="item.title"
                  :is-multiple="item.type == 'files'"
                  :is-download-only="editIsDisabled(item.uuid)"
                  is-commentable
                  :uuid="item.uuid"
                  :can-comment="canComment"
                />
                <PhotoUpload
                  v-else-if="['images'].includes(item.type)"
                  :id="item.uuid"
                  :uuid="item.uuid"
                  v-model="item['value']"
                  :photos-uploaded="item['value']"
                  :title="item.title"
                  is-multiple
                  is-commentable
                  :is-disabled="editIsDisabled(item.uuid)"
                  :can-comment="canComment"
                />
                <div
                  :class="`${item.classnames}`"
                  v-else-if="item.type === 'switcher'"
                >
                  <Switcher
                    :uuid="item.uuid"
                    :can-comment="canComment"
                    :is-commentable="commentsVisible"
                    :is-inactive="true"
                    v-model="item.value"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="voteSchedule !== 'none'" class="voting-date__block">
        <div v-if="voteSchedule === 'edit'" class="voting-date__block">
          <h4 class="title-h4 profile__title">
            {{ $t('categorization.voting_date_title') }}
          </h4>
          <p class="subtitle">
            {{ $t('categorization.voting_date_subtitle') }}
          </p>
        </div>
        <div v-if="voteSchedule === 'view'" class="voting-date__block">
          <h4 class="title-h4 profile__title">
            {{ $t('categorization.voting_date_title_view') }}
          </h4>
          <p class="subtitle">
            {{ $t('categorization.voting_date_subtitle_view') }}
          </p>
        </div>
        <FileUpload
          v-model="uploadedSchedule"
          class="schedule__upload"
          :is-multiple="false"
          :valid-formats="['doc', 'docx', 'pdf', 'img', 'jpeg', 'jpg', 'png']"
          :is-download-only="voteSchedule == 'view'"
          :max-size-k-b="25000"
        />
        <div class="">
          <DatePicker
            v-model="votingDate"
            :format="'DD-MM-YYYY'"
            value-type="YYYY-MM-DD HH:mm:ss"
            lang="datePickerConfig"
            :disabled="voteSchedule === 'view'"
            :placeholder="$t(`categorization.voting_date_placeholder`)"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <LoaderContent />
    </template>
    <div class="no-print">
      <ButtonsBlock
        :buttons="buttonsArray"
        :disabledDownLoad="!isCertificate"
        :disabledAchive="!isAchive"
        :disabledVote="isAfterToday"
        @print="print()"
        @downloadCertificate="downloadCertificate()"
        @preliminaryResults="preliminaryResultsHandler()"
        @downloadPDF="downloadPDF()"
        @vote="voteHandler()"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getCategorizationFormStructure } from '@/api/services/hotel.js';
import { checkIsSameDate, checkIsDateSameOrBeforeToday, checkIsDateAfterToday } from '@/library/time';
import DatePicker from 'vue2-datepicker';
import FileUpload from '@/elements/FileUpload.vue';
import PhotoUpload from '@/elements/PhotoUpload.vue';
import ButtonsBlock from '@/elements/ButtonsBlock.vue';
import Switcher from '@/elements/Switcher.vue';
import LoaderContent from '@/elements/LoaderContent.vue';

export default {
  components: {
    FileUpload,
    PhotoUpload,
    DatePicker,
    ButtonsBlock,
    Switcher,
    LoaderContent,
  },
  data() {
    return {
      buttonsArray: [],
      commentsVisible: false,
      canComment: false,
      canEdit: '',
      voteSchedule: '',
      dataToSend: new FormData(),
      uploadedSchedule: [],
      votingDate: '',
      structure: null,
    };
  },

  async created() {
    await this.getCategorizationStructure();

    let categorizationRequestUUUID = this.$route.params.id;

    await this.$store.dispatch(
      'getCategorizationDeputyPresiding',
      categorizationRequestUUUID
    );

    this.statusModelHandler();

    if (this.commentsVisible) {
      await this.$store.dispatch(
        'getCommentsDeputyPresiding',
        categorizationRequestUUUID
      );
    }
    this.populateFormWithData();
    this.getScheduleData();
  },

  computed: {
    ...mapGetters([
      'getCategorizationDeputyPresiding',
      'getCommentsDeputyPresiding',
      'getStatusModel',
    ]),

    prefilledCriteris() {
      let prefilledCriteris = {};
      this.getCategorizationDeputyPresiding?.criteria?.forEach((criteria) => {
        prefilledCriteris[criteria.categorization_criterion_uuid] = criteria;
      });
      return prefilledCriteris;
    },

    ifToday() {
      return checkIsSameDate(this.getCategorizationDeputyPresiding.voting_at);
    },

    ifBeforeOrToday() {
      return checkIsDateSameOrBeforeToday(
        this.getCategorizationDeputyPresiding?.voting_at
      );
    },

    isAfterToday() {
      return checkIsDateAfterToday(this.getCategorizationPresiding?.voting_at);
    },

    isCertificate() {
      return !!this.getCategorizationDeputyPresiding?.certificate;
    },

    isAchive() {
      return !!this.getCategorizationDeputyPresiding?.archive;
    },
  },

  methods: {
    async getCategorizationStructure() {
      let data = { lang: 'uk', uuid: this.$route.params.id };
      try {
        let res;
        res = await getCategorizationFormStructure(data);
        if (res.status === 200) {
          this.structure = res.data.data.structure;
        }
      } catch (e) {
        console.log('>>> error getCategorizationFormStructure', e.response);
      }
    },

    print() {
      window.print();
    },

    statusModelHandler() {
      this.getStatusModel.forEach((element) => {
        if (
          element.filing_status ==
            this.getCategorizationDeputyPresiding.filing_status &&
          element.review_status ==
            this.getCategorizationDeputyPresiding.review_status
        ) {
          this.commentsVisible = element.comments_visible || this.ifToday;
          this.canComment =
            element.can_comment ||
            (this.ifToday &&
              this.getCategorizationDeputyPresiding.review_status ==
                'waiting_for_voting');
          this.canEdit = element.edit;
          this.voteSchedule = element.schedule;

          if (Array.isArray(element.buttons)) {
            this.buttonsArray = element.buttons;
          } else if (this.ifBeforeOrToday) {
            console.log(this.getCategorizationDeputyPresiding)
            this.buttonsArray = this.getCategorizationDeputyPresiding?.is_voted
              ? [...element.buttons.buttonsAfterVoting]
              : [...element.buttons.buttonsDuringVoting];
          } else {
            this.buttonsArray = [...element.buttons.buttonsBeforeVoting];
          }
          
          // else {
          //   this.buttonsArray = this.ifBeforeOrToday
          //     ? [...element.buttons.buttonsDuringVoting]
          //     : [...element.buttons.buttonsBeforeVoting];
          // }
        }
      });
    },

    editIsDisabled(uuid) {
      let editIsDisabled = false;
      switch (this.canEdit) {
        case 'can_edit_all_fields':
          editIsDisabled = false;
          break;
        case 'not_allowed':
          editIsDisabled = true;
          break;
        case 'can_edit_commented_fields':
          this.getCommentsHotel[uuid]
            ? (editIsDisabled = false)
            : (editIsDisabled = true);
          break;
      }
      return editIsDisabled;
    },

    populateFormWithData() {
      this.structure.map((el) =>
        el.subgroups.map((s) =>
          s.criterias.map(async (s) => {
            if (s.uuid in this.prefilledCriteris) {
              if (Array.isArray(this.prefilledCriteris[s.uuid].value)) {
                let valueToPopulate = [];

                for (let file in this.prefilledCriteris[s.uuid].value) {
                  let convertedFile = await this.createFile(
                    this.prefilledCriteris[s.uuid].value[file].original_url,
                    this.prefilledCriteris[s.uuid].value[file].mime
                  );
                  valueToPopulate.push(convertedFile);
                }
                s.value = valueToPopulate;
              } else {
                s.value = this.prefilledCriteris[s.uuid].value;
              }
            }
          })
        )
      );
    },

    async getScheduleData() {
      this.votingDate = !!this.getCategorizationDeputyPresiding?.voting_at
        ? this.getCategorizationDeputyPresiding?.voting_at
        : this.votingDate;

      this.uploadedSchedule = !!this.getCategorizationDeputyPresiding
        ?.schedule_attachment?.length
        ? [
            await this.createFile(
              this.getCategorizationDeputyPresiding.schedule_attachment[0]
                .original_url,
              this.getCategorizationDeputyPresiding.schedule_attachment[0].mime
            ),
          ]
        : this.uploadedSchedule;
    },

    async createFile(url, type) {
      const response = await fetch(url);
      const data = await response.blob();
      const metadata = {
        type: type,
      };
      return new File([data], url.split('/').at(-1), metadata);
    },

    signAndSend() {
      if (!this.validationPassed()) return;
      this.dataToSend.append(
        `uuid`,
        this.getCategorizationDeputyPresiding.uuid
      );
      this.dataToSend.append(`voting_date`, this.votingDate);
      if (this.uploadedSchedule[0]) {
        this.dataToSend.append(`schedule_attachment`, this.uploadedSchedule[0]);
      }
      this.$store.dispatch('setVotingDateSecretary', this.dataToSend);
      this.$store.dispatch(
        'signVotingDateSecretary',
        this.getCategorizationDeputyPresiding.uuid
      );
    },

    validationPassed() {
      if (!this.votingDate) {
        this.votingDateError = true;
        return false;
      }

      if (new Date(this.votingDate).getTime() < Date.now()) {
        this.votingDateError = true;
        this.votingDateErrorText = this.$t('error.date_must_be_after_today');
        return false;
      }

      if (this.uploadedSchedule.length == 0) {
        this.uploadedScheduleError = true;
        return false;
      }

      return true;
    },

    sendToComissionHead() {
      if (!this.validationPassed()) return;
      this.dataToSend.append(
        `uuid`,
        this.getCategorizationDeputyPresiding.uuid
      );
      this.dataToSend.append(`voting_date`, this.votingDate);
      if (this.uploadedSchedule[0]) {
        this.dataToSend.append(`schedule_attachment`, this.uploadedSchedule[0]);
      }

      this.$store.dispatch('setVotingDateSecretary', this.dataToSend);
    },

    preliminaryResultsHandler() {
      this.$router.push({
        name: 'deputy-presiding-commissioner_voting-results',
        params: {
          id: this.getCategorizationDeputyPresiding.voting.uuid,
        },
      });
    },

    voteHandler() {
      this.$store.commit('modal/SET_MODAL', {
        state: true,
        name: 'vote',
        size: 'xxl',
        handler: {
          payload: this.getCategorizationDeputyPresiding.voting.uuid,
          role: 'DeputyPresiding',
        },
      });
    },

    groupGrid(group) {
      if (group.criterias[0].type == 'images') {
        return 'image__grid';
      }
      return 'profile__grid';
    },

    downloadCertificate() {
      this.$store.dispatch(
        'downloadFile',
        this.getCategorizationDeputyPresiding.certificate.attachment
          .original_url
      );
    },

    downloadPDF() {
      this.$store.dispatch(
        'downloadFile',
        this.getCategorizationDeputyPresiding.archive.original_url
      );
    },
  },
};
</script>

<style lang="sass" scoped>
.voting-date__block
  display: flex
  align-items: center
  flex-direction: column
  margin-top: 4rem
.schedule__upload
  max-width: 40rem
  margin: 7rem 0
.mx-datepicker
  max-width: 40rem
.control__btn-wrapper
  margin-top: 8rem
</style>
